import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Dashboard } from '@src/main/modules/common/dashboard/domain/Dashboard';
import { User } from '@src/main/modules/common/user/domain/User';
import { useDashboardContext } from '@src/main/ui/providers/dashboard/DashboardContextProvider';
import { useSearchContext } from '@src/main/ui/providers/search/SearchContextProvider';
import { useUserContext } from '@src/main/ui/providers/user/UserContextProvider';

import { tagsToString } from '@src/main/ui/components/common/Dashboard/common/fields/tags/tagsToString';

interface DashboardDataToSearch {
  keyTags: string;
  name: string;
  tags: string;
  description: string;
  area: string;
  dashboard: Dashboard;
}

export const useSearch = () => {
  const { dashboards } = useDashboardContext();
  const { users } = useUserContext();
  const { searchInput, setSearchInput, setSearchInputLocal } = useSearchContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const [dashboardsDataToSearch, setDashboardsDataToSearch] = useState<DashboardDataToSearch[]>([]);
  const [filteredDashboardsTopResults, setFilteredDashboardsTopResults] = useState<
    Dashboard[] | undefined
  >(undefined);
  const [filteredDashboardsOtherResults, setFilteredDashboardsOtherResults] = useState<
    Dashboard[] | undefined
  >(undefined);

  const [usersDataToSearch, setUsersDataToSearch] = useState<User[]>([]);
  const [filtederUsersResults, setFilteredUsersResults] = useState<User[] | undefined>(undefined);

  useEffect(() => {
    const search = searchParams.get('search');
    setSearchInput(search || '');
    setSearchInputLocal(search || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!searchInput) {
      setFilteredDashboardsTopResults(undefined);
      setFilteredDashboardsOtherResults(undefined);
      return;
    }

    const topResults = getFilteredDashboards(dashboardsDataToSearch, searchInput, [
      'name',
      'keyTags',
    ]);
    setFilteredDashboardsTopResults(topResults);

    const notInTopResults = dashboardsDataToSearch.filter(
      (dashboardDataToSearch) =>
        !topResults.map((topResult) => topResult.id).includes(dashboardDataToSearch.dashboard.id),
    );
    const otherResults = getFilteredDashboards(notInTopResults, searchInput, [
      'tags',
      'description',
      'area',
    ]);
    setFilteredDashboardsOtherResults(otherResults);
  }, [dashboardsDataToSearch, searchInput]);

  useEffect(() => {
    if (!searchInput) {
      setFilteredUsersResults(users);
      return;
    }

    const usersResults = filterUsers(searchInput, usersDataToSearch);
    setFilteredUsersResults(usersResults);
  }, [users, searchInput, usersDataToSearch]);

  useEffect(() => {
    setSearchParams({ search: searchInput });
  }, [searchInput, setSearchParams]);

  useEffect(() => {
    if (dashboards) {
      setDashboardsDataToSearch(
        dashboards.map((dashboard) => ({
          keyTags: tagsToString(dashboard.keyTags).toLocaleLowerCase(),
          name: dashboard.name.toLocaleLowerCase(),
          tags: tagsToString(dashboard.tags).toLocaleLowerCase(),
          description: dashboard.description?.toLocaleLowerCase() || '',
          area: getAreaAsString(dashboard).toLocaleLowerCase(),
          dashboard,
        })),
      );
    }
  }, [dashboards]);

  useEffect(() => {
    if (users) {
      setUsersDataToSearch(
        users.map((user) => ({
          id: user.id,
          name: user.name,
          recentlyOpened: user.recentlyOpened,
          favorites: user.favorites,
          recommended: user.recommended,
          createdAt: user.createdAt,
          createdBy: user.createdBy,
          updatedAt: user.updatedAt,
          updatedBy: user.updatedBy,
          roles: user.roles,
        })),
      );
    }
  }, [users]);

  return {
    filteredDashboardsTopResults,
    filteredDashboardsOtherResults,
    filtederUsersResults,
    searchInput,
  };
};

const getFilteredDashboards = (
  dashboardsDataToSearch: DashboardDataToSearch[],
  value: string,
  fields: (keyof DashboardDataToSearch)[],
): Dashboard[] => {
  if (value === '') return [];

  const valueLowerCase = value.toLocaleLowerCase();

  return dashboardsDataToSearch
    .filter((dashboardDataToSearch) =>
      fields.some((field) => compareDashboardsField(valueLowerCase, field, dashboardDataToSearch)),
    )
    .map((dashboardDataToSearch) => dashboardDataToSearch.dashboard);
};

const compareDashboardsField = (
  value: string,
  field: keyof DashboardDataToSearch,
  dashboardDataToSearch: DashboardDataToSearch,
): boolean => {
  const searchWithoutSpaces: (keyof DashboardDataToSearch)[] = ['tags', 'keyTags'];
  const fieldValueString = dashboardDataToSearch[field]?.toString() || '';

  if (searchWithoutSpaces.includes(field)) {
    return fieldValueString.includes(value) || fieldValueString.includes(value.replace(/\s/g, ''));
  }

  return fieldValueString.includes(value);
};

const getAreaAsString = (dashboard: Dashboard) =>
  `${dashboard.area?.name} - ${dashboard.area?.subareaName} - ${dashboard.area?.subsubareaName}`;

const filterUsers = (searchTerm: string, users: User[]) => {
  const filtered = users.filter((user) => {
    const nameMatch = user.name.toLowerCase().includes(searchTerm.toLowerCase());
    const roleMatch = user.roles.some((role) =>
      role.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    return nameMatch || roleMatch;
  });

  return filtered;
};
